import { useEffect, useState, type ReactNode } from 'react';
import toast from 'react-hot-toast';
import {
  Button,
  Card,
  EmptyState,
  Input,
  LoadingState,
  PageHeader,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeaderCell,
  TableRow,
} from '@/components/ui';
import { getErrorMessage } from '@/api';
import { SALES_PLATFORMS } from '@/api/admin';
import type { AdminDecorationMethod, SalesPlatform } from '@/api/admin';
import { isValidPrice } from '@/lib/decoration-price';
import {
  useAdminDecorationMethods,
  useSetDecorationBasePrice,
} from '@/hooks/useAdminDecorationMethods';

const PLATFORM_LABEL: Record<SalesPlatform, string> = {
  DIRECT_SALE: 'Direct Sale',
  SELLER_CREATOR: 'Seller Creator',
};

/**
 * Admin: decoration method base prices.
 *
 * The catalog itself is seed-managed — methods cannot be created, renamed
 * or deleted here. Prices are maintained per method and platform; 0 is a
 * real price (free of charge) and "unpriced" is its own explicit action.
 * The admin shell carries the navigation, so the page starts with the kit
 * page header.
 */
export function AdminDecorationMethodsPage() {
  const {
    data: methods,
    isLoading,
    isError,
    refetch,
  } = useAdminDecorationMethods();

  let content: ReactNode;
  if (isLoading) {
    content = <LoadingState label="Loading decoration methods…" />;
  } else if (isError || !methods) {
    content = (
      <Card className="flex flex-col items-center gap-4 text-center">
        <p className="text-sm text-danger">
          Failed to load decoration methods.
        </p>
        <Button variant="outline" onClick={() => void refetch()}>
          Retry
        </Button>
      </Card>
    );
  } else if (methods.length === 0) {
    content = (
      <EmptyState
        title="No decoration methods"
        description="The catalog is seed-managed and has no methods yet."
      />
    );
  } else {
    content = (
      <>
        <Card padding="none">
          <Table>
            <TableHead>
              <TableRow>
                <TableHeaderCell>Method</TableHeaderCell>
                <TableHeaderCell>Phoenix ID</TableHeaderCell>
                {SALES_PLATFORMS.map((platform) => (
                  <TableHeaderCell key={platform}>
                    {PLATFORM_LABEL[platform]}
                  </TableHeaderCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {methods.map((method) => (
                <TableRow key={method.id}>
                  <TableCell className="align-top">
                    <div className="font-medium text-ink">{method.name}</div>
                    <div className="font-mono text-xs text-mute">
                      {method.code}
                    </div>
                  </TableCell>
                  <TableCell className="align-top font-mono text-mute">
                    {method.externalId ?? '—'}
                  </TableCell>
                  {SALES_PLATFORMS.map((platform) => (
                    <TableCell key={platform} className="align-top">
                      <BasePriceField method={method} platform={platform} />
                    </TableCell>
                  ))}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Card>

        <p className="text-xs text-mute">
          0 is a real price and offers the decoration free of charge. Use “Mark
          unpriced” to stop offering a method on a platform.
        </p>
      </>
    );
  }

  return (
    <div className="mx-auto flex w-full max-w-5xl flex-col gap-6">
      <PageHeader
        eyebrow="Admin"
        title="Decoration methods"
        description="Base prices per platform. Products can override them individually."
      />
      {content}
    </div>
  );
}

interface BasePriceFieldProps {
  method: AdminDecorationMethod;
  platform: SalesPlatform;
}

function BasePriceField({ method, platform }: BasePriceFieldProps) {
  const current =
    method.platformPrices.find((row) => row.platform === platform)?.price ??
    null;

  const toInput = (v: string | null) =>
    v === null ? '' : Number(v).toFixed(2);

  const [draft, setDraft] = useState(() => toInput(current));
  const { mutateAsync, isPending } = useSetDecorationBasePrice();

  useEffect(() => {
    setDraft(toInput(current));
  }, [method.id, platform, current]);

  const isDirty = draft.trim() !== toInput(current);
  const parsed = draft.trim() === '' ? null : Number(draft);
  const isValid = parsed !== null && isValidPrice(parsed);
  const inputId = `base-price-${method.id}-${platform}`;
  const errorId = `${inputId}-error`;
  const showError = isDirty && !isValid;

  // Clearing the input never writes: `type="number"` reports unparseable
  // input (a typed "5,46") as an empty string, so a Save on empty would mark
  // a method unpriced when the admin meant to change its price. Unpricing is
  // its own action below.
  const save = async (price: number | null) => {
    try {
      await mutateAsync({ methodId: method.id, platform, price });
      toast.success(price === null ? 'Marked unpriced' : 'Base price saved');
    } catch (error) {
      toast.error(getErrorMessage(error));
    }
  };

  return (
    <div className="space-y-1">
      <div className="flex items-center gap-2">
        <div className="relative w-32">
          <label htmlFor={inputId} className="sr-only">
            {`${method.name} — ${PLATFORM_LABEL[platform]}`}
          </label>
          <Input
            id={inputId}
            type="number"
            min={0}
            step="0.01"
            inputMode="decimal"
            value={draft}
            placeholder="unpriced"
            error={showError}
            aria-describedby={showError ? errorId : undefined}
            onChange={(e) => setDraft(e.target.value)}
            className="h-9 pr-7 text-right font-mono text-sm"
          />
          <span
            className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-xs text-mute"
            aria-hidden="true"
          >
            €
          </span>
        </div>
        <Button
          size="sm"
          onClick={() => parsed !== null && isValid && save(parsed)}
          disabled={!isDirty || !isValid || isPending}
        >
          {isPending ? 'Saving…' : 'Save'}
        </Button>
      </div>
      {showError ? (
        <p id={errorId} role="alert" className="text-xs text-danger">
          {parsed === null
            ? 'Enter an amount, or use “Mark unpriced”.'
            : 'Enter 0 or a positive amount with at most two decimals.'}
        </p>
      ) : (
        current !== null && (
          <button
            type="button"
            onClick={() => save(null)}
            disabled={isPending}
            className="text-xs font-medium text-primary hover:underline disabled:opacity-40"
          >
            Mark unpriced
          </button>
        )
      )}
    </div>
  );
}
